import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  inject,
} from '@angular/core';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  AlertsComponent,
  AlertsService,
  CurrencyComponent,
  ImageThumbnailComponent,
} from '@web/shared/ui';
import { ReactiveFormsModule } from '@angular/forms';
import {
  PublicAvailability,
  PublicCartItem,
  PublicCartQuantityUpdateComponent,
  PublicCartRemoveButtonComponent,
  PublicProductNameComponent,
  PublicQuantityChangeFailureEvent,
} from '@web/shared/module/shop';
import { isNil } from '@web/utils';
import { SummernoteHtmlPreviewComponent } from '@web/shared/external';

@Component({
  selector: 'app-public-order-checkout-item',
  imports: [
    AlertsComponent,
    CurrencyComponent,
    ImageThumbnailComponent,
    NgOptimizedImage,
    PublicProductNameComponent,
    PublicCartQuantityUpdateComponent,
    PublicCartRemoveButtonComponent,
    ReactiveFormsModule,
    SummernoteHtmlPreviewComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './public-order-checkout-item.component.html',
  styleUrl: './public-order-checkout-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PublicOrderCheckoutItemComponent {
  private readonly alertsService = inject(AlertsService);

  public readonly item = input.required<PublicCartItem>();

  protected readonly id = computed(() => this.item().id);
  protected readonly imageAlt = computed(() => this.item().image.alt);
  protected readonly imageUrl = computed(() => this.item().image.url);
  protected readonly name = computed(() => this.item().name);
  protected readonly subName = computed(() => this.item().subName);
  protected readonly price = computed(() => this.item().price);
  protected readonly quantity = computed(() => this.item().quantity);
  protected readonly slug = computed(() => this.item().slug);
  protected readonly status = computed(() => this.item().status);
  protected readonly type = computed(() => this.item().type);
  protected readonly unitPrice = computed(() => this.item().unitPrice);
  protected readonly attributes = computed(() => this.item().attributes);

  public constructor() {
    effect(() => this.tryToShowFailureAlertWithUnavailableMessage());
  }

  public handleQuantityChangeSuccessEvent(): void {
    this.alertsService.clear();
  }

  public handleQuantityChangeFailureEvent(
    event: PublicQuantityChangeFailureEvent,
  ): void {
    this.showFailureAlert(event.multilineReasons);
  }

  private tryToShowFailureAlertWithUnavailableMessage(): void {
    const { unavailableMessage, availability } = this.status();

    if (availability === PublicAvailability.Available) {
      return;
    }

    const message = unavailableMessage;

    if (isNil(message)) {
      return;
    }

    this.showFailureAlert(message);
  }

  private showFailureAlert(message: string): void {
    this.alertsService.showSingle((builder) => builder.buildFailure(message));
  }
}
